import { useState, useEffect, useRef } from "react";
import { format } from "date-fns";
import axios from "axios";
import { toast } from "sonner";
import { API } from "@/App";
import { activityUpdate, leadDetails } from "../lib/lead-activity";
import { formatErrorDetail } from "../lib/format-error";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Phone, MessageSquare, Calendar, Plus, Clock, Pencil, Trash2, AlertTriangle, Loader2, PhoneCall, ThumbsUp, PhoneMissed, Voicemail, PhoneForwarded, CalendarCheck, ThumbsDown, PhoneOff, ArrowDownLeft, ArrowUpRight, ChevronLeft, ChevronRight, ExternalLink, Globe, Instagram, MessageCircle, History } from "lucide-react";
import MentionSelector from "./MentionSelector";
import MultiSelectDropdown from "./MultiSelectDropdown";

const CATEGORIES = ["Architectural", "Residential", "Renovations", "Project Homes", "Commercial"];

// Call outcome options with icons and colors
const CALL_OUTCOMES = [
  { value: "answered", label: "Answered", icon: ThumbsUp, color: "bg-green-100 text-green-700 border-green-300 hover:bg-green-200" },
  { value: "no_answer", label: "No Answer", icon: PhoneMissed, color: "bg-slate-100 text-slate-700 border-slate-300 hover:bg-slate-200" },
  { value: "voicemail", label: "Voicemail Left", icon: Voicemail, color: "bg-blue-100 text-blue-700 border-blue-300 hover:bg-blue-200" },
  { value: "callback", label: "Asked to Call Back", icon: PhoneForwarded, color: "bg-amber-100 text-amber-700 border-amber-300 hover:bg-amber-200" },
  { value: "number_busy", label: "Number Busy", icon: PhoneOff, color: "bg-orange-100 text-orange-700 border-orange-300 hover:bg-orange-200" },
  { value: "invalid_number", label: "Invalid Number", icon: PhoneOff, color: "bg-gray-100 text-gray-700 border-gray-300 hover:bg-gray-200" }
];

const getOutcomeDisplay = (outcome) => {
  const found = CALL_OUTCOMES.find(o => o.value === outcome);
  return found || { label: outcome, color: "bg-slate-100 text-slate-700" };
};

const LeadModal = ({ isOpen, onClose, lead, onSave, teamMembers = [], userRole = "sales_rep", onActivityUpdate, leads = [], onNavigate, initialTab = null, saveError = "" }) => {
  const [formData, setFormData] = useState({
    company_name: "", first_name: "", last_name: "", title: "", mobile: "", phone_number: "",
    email: "", website: "", instagram: "", facebook: "", status: "New", category: [], notes: "",
    call_logs: [], text_logs: [], meetings: [], scheduled_calls: [], comments: [], status_history: [], assigned_to: "", assigned_to_name: ""
  });
  const [activeTab, setActiveTab] = useState(initialTab || "calls");
  const [newCallNote, setNewCallNote] = useState("");
  const [newCallOutcome, setNewCallOutcome] = useState("");
  const [newCallDirection, setNewCallDirection] = useState("outbound");
  const [newTextNote, setNewTextNote] = useState("");
  const [newTextDirection, setNewTextDirection] = useState("outbound");
  const [newMeetingNote, setNewMeetingNote] = useState("");
  const [newMeetingDate, setNewMeetingDate] = useState("");
  const [newMeetingTime, setNewMeetingTime] = useState("09:00");
  
  // Scheduled call state
  const [newScheduledCallDate, setNewScheduledCallDate] = useState("");
  const [newScheduledCallTime, setNewScheduledCallTime] = useState("09:00");
  const [newScheduledCallNote, setNewScheduledCallNote] = useState("");
  
  // Comment state
  const [newComment, setNewComment] = useState("");
  
  // Edit state
  const [editingCall, setEditingCall] = useState(null);
  const [editingText, setEditingText] = useState(null);
  const [editingMeeting, setEditingMeeting] = useState(null);
  const [editingScheduledCall, setEditingScheduledCall] = useState(null);
  const [editingComment, setEditingComment] = useState(null);
  
  // Delete confirmation state
  const [deleteConfirm, setDeleteConfirm] = useState({ open: false, type: null, index: null });
  
  // Saving state
  const [savingActivity, setSavingActivity] = useState(false);
  const [activityError, setActivityError] = useState("");
  const activeLeadId = useRef(lead?.lead_id);
  activeLeadId.current = lead?.lead_id;

  const allStatuses = ["New", "Attempted Contact", "Contacted", "Not Yet", "Interested", "Not Interested", "Meeting Scheduled", "Proposal Sent", "Won", "Lost", "Invalid Details"];
  const restrictedStatuses = ["Proposal Sent", "Won", "Lost", "Invalid Details"];
  
  // Get available statuses based on user role and current status
  const getAvailableStatuses = () => {
    if (userRole === "manager") {
      return allStatuses;
    }
    // Sales rep: can't select restricted statuses, and can't change if current status is restricted
    const currentStatus = formData.status;
    if (restrictedStatuses.includes(currentStatus)) {
      // Locked - can only show current status
      return [currentStatus];
    }
    // Filter out restricted statuses
    return allStatuses.filter(s => !restrictedStatuses.includes(s));
  };
  
  const isStatusLocked = userRole !== "manager" && restrictedStatuses.includes(formData.status);
  
  // Navigation logic
  const currentLeadIndex = lead && leads.length > 0 ? leads.findIndex(l => l.lead_id === lead.lead_id) : -1;
  const hasPreviousLead = currentLeadIndex > 0;
  const hasNextLead = currentLeadIndex >= 0 && currentLeadIndex < leads.length - 1;
  
  const handlePreviousLead = () => {
    if (hasPreviousLead && onNavigate) {
      onNavigate(leads[currentLeadIndex - 1]);
    }
  };
  
  const handleNextLead = () => {
    if (hasNextLead && onNavigate) {
      onNavigate(leads[currentLeadIndex + 1]);
    }
  };
  
  const token = localStorage.getItem("token");

  useEffect(() => {
    if (lead) {
      setFormData({
        company_name: lead.company_name || "",
        first_name: lead.first_name || "",
        last_name: lead.last_name || "",
        title: lead.title || "",
        mobile: lead.mobile || "",
        phone_number: lead.phone_number || "",
        email: lead.email || "",
        website: lead.website || "",
        instagram: lead.instagram || "",
        facebook: lead.facebook || "",
        status: lead.status || "New",
        category: Array.isArray(lead.category) ? lead.category : (lead.category ? [lead.category] : []),
        notes: lead.notes || lead.caller_notes || "",
        call_logs: lead.call_logs || [],
        text_logs: lead.text_logs || [],
        meetings: lead.meetings || [],
        scheduled_calls: lead.scheduled_calls || [],
        comments: lead.comments || [],
        status_history: lead.status_history || [],
        assigned_to: lead.assigned_to || "",
        assigned_to_name: lead.assigned_to_name || ""
      });
    } else {
      setFormData({
        company_name: "", first_name: "", last_name: "", title: "", mobile: "", phone_number: "",
        email: "", website: "", instagram: "", facebook: "", status: "New", category: [], notes: "",
        call_logs: [], text_logs: [], meetings: [], scheduled_calls: [], comments: [], status_history: [], assigned_to: "", assigned_to_name: ""
      });
    }
    resetForms();
    setActivityError("");
  }, [lead, isOpen]);

  // Set active tab when initialTab prop changes (e.g., from notification click)
  useEffect(() => {
    if (initialTab && isOpen) {
      setActiveTab(initialTab);
    } else if (!initialTab && isOpen) {
      setActiveTab("calls"); // Default tab when not from notification
    }
  }, [initialTab, isOpen]);

  const resetForms = () => {
    setNewCallNote("");
    setNewCallOutcome("");
    setNewCallDirection("outbound");
    setNewTextNote("");
    setNewTextDirection("outbound");
    setNewMeetingNote("");
    setNewMeetingDate("");
    setNewMeetingTime("09:00");
    setNewScheduledCallDate("");
    setNewScheduledCallTime("09:00");
    setNewScheduledCallNote("");
    setNewComment("");
    setEditingCall(null);
    setEditingText(null);
    setEditingMeeting(null);
    setEditingScheduledCall(null);
    setEditingComment(null);
  };

  // Save activity to database immediately for existing leads
  const saveActivityToDb = async (updatedFormData) => {
    if (!lead?.lead_id) {
      setFormData(updatedFormData);
      return true;
    }
    const targetId = lead.lead_id;
    setActivityError("");
    setSavingActivity(true);
    try {
      const response = await axios.put(
        `${API}/leads/${lead.lead_id}`,
        activityUpdate(formData, updatedFormData),
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (activeLeadId.current !== targetId) return false;

      // Update local state with backend response (includes logged_by_name)
      if (response.data) {
        setFormData(prev => ({
          ...prev,
          call_logs: response.data.call_logs || prev.call_logs,
          text_logs: response.data.text_logs || prev.text_logs,
          meetings: response.data.meetings || prev.meetings,
          scheduled_calls: response.data.scheduled_calls || prev.scheduled_calls,
          comments: response.data.comments || prev.comments
        }));
      }
      
      if (onActivityUpdate) {
        onActivityUpdate();
      }
      toast.success("Activity saved");
      return true;
    } catch (error) {
      console.error("Error saving activity:", error);
      const message = formatErrorDetail(error, "Activity was not saved. Your draft is still here; try again.");
      if (activeLeadId.current === targetId) setActivityError(message);
      toast.error(message);
      if (error.response?.status === 409 && onActivityUpdate) onActivityUpdate();
      return false;
    } finally {
      setSavingActivity(false);
    }
  };

  // Scheduled call handlers
  const handleAddScheduledCall = async () => {
    if (!newScheduledCallDate) return;
    const scheduledDateTime = new Date(`${newScheduledCallDate}T${newScheduledCallTime}`).toISOString();
    const newScheduledCall = { date: scheduledDateTime, notes: newScheduledCallNote };
    const updatedFormData = { ...formData, scheduled_calls: [...formData.scheduled_calls, newScheduledCall] };
    if (!await saveActivityToDb(updatedFormData)) return;
    setNewScheduledCallNote("");
    setNewScheduledCallDate("");
    setNewScheduledCallTime("09:00");

  };

  const handleEditScheduledCall = (index) => {
    const call = formData.scheduled_calls[index];
    const dateObj = new Date(call.date);
    setEditingScheduledCall({
      index,
      date: dateObj.toISOString().split('T')[0],
      time: dateObj.toTimeString().slice(0, 5),
      notes: call.notes || ""
    });
  };

  const handleSaveScheduledCallEdit = async () => {
    const newScheduledCalls = [...formData.scheduled_calls];
    const scheduledDateTime = new Date(`${editingScheduledCall.date}T${editingScheduledCall.time}`).toISOString();
    newScheduledCalls[editingScheduledCall.index] = { ...formData.scheduled_calls[editingScheduledCall.index], date: scheduledDateTime, notes: editingScheduledCall.notes };
    const updatedFormData = { ...formData, scheduled_calls: newScheduledCalls };
    if (!await saveActivityToDb(updatedFormData)) return;
    setEditingScheduledCall(null);

  };

  // Call handlers
  const handleAddCall = async () => {
    if (!newCallOutcome) return;
    const newCall = {
      date: new Date().toISOString(),
      notes: newCallNote,
      outcome: newCallOutcome,
      direction: newCallDirection
    };
    const updatedFormData = { ...formData, call_logs: [...formData.call_logs, newCall] };
    if (!await saveActivityToDb(updatedFormData)) return;
    setNewCallNote("");
    setNewCallOutcome("");
    setNewCallDirection("outbound");

  };

  const handleEditCall = (index) => {
    const call = formData.call_logs[index];
    setEditingCall({ index, ...call, outcome: call.outcome || "", direction: call.direction || "outbound" });
  };

  const handleSaveCallEdit = async () => {
    const newLogs = [...formData.call_logs];
    newLogs[editingCall.index] = {
      ...formData.call_logs[editingCall.index],
      date: editingCall.date,
      notes: editingCall.notes,
      outcome: editingCall.outcome,
      direction: editingCall.direction || "outbound"
    };
    const updatedFormData = { ...formData, call_logs: newLogs };
    if (!await saveActivityToDb(updatedFormData)) return;
    setEditingCall(null);

  };

  // Text handlers
  const handleAddText = async () => {
    if (!newTextNote.trim()) return;
    const newText = { date: new Date().toISOString(), notes: newTextNote, direction: newTextDirection };
    const updatedFormData = { ...formData, text_logs: [...formData.text_logs, newText] };
    if (!await saveActivityToDb(updatedFormData)) return;
    setNewTextNote("");
    setNewTextDirection("outbound");

  };

  const handleEditText = (index) => {
    const text = formData.text_logs[index];
    setEditingText({ index, ...text, direction: text.direction || "outbound" });
  };

  const handleSaveTextEdit = async () => {
    const newLogs = [...formData.text_logs];
    newLogs[editingText.index] = { ...formData.text_logs[editingText.index], date: editingText.date, notes: editingText.notes, direction: editingText.direction || "outbound" };
    const updatedFormData = { ...formData, text_logs: newLogs };
    if (!await saveActivityToDb(updatedFormData)) return;
    setEditingText(null);

  };

  // Meeting handlers
  const handleAddMeeting = async () => {
    if (!newMeetingDate) return;
    const meetingDateTime = new Date(`${newMeetingDate}T${newMeetingTime}`).toISOString();
    const newMeeting = { date: meetingDateTime, notes: newMeetingNote };
    const updatedFormData = { ...formData, meetings: [...formData.meetings, newMeeting] };
    if (!await saveActivityToDb(updatedFormData)) return;
    setNewMeetingNote("");
    setNewMeetingDate("");
    setNewMeetingTime("09:00");

  };

  const handleEditMeeting = (index) => {
    const meeting = formData.meetings[index];
    const dateObj = new Date(meeting.date);
    setEditingMeeting({
      index,
      date: dateObj.toISOString().split('T')[0],
      time: dateObj.toTimeString().slice(0, 5),
      notes: meeting.notes || ""
    });
  };

  const handleSaveMeetingEdit = async () => {
    const newMeetings = [...formData.meetings];
    const meetingDateTime = new Date(`${editingMeeting.date}T${editingMeeting.time}`).toISOString();
    newMeetings[editingMeeting.index] = { ...formData.meetings[editingMeeting.index], date: meetingDateTime, notes: editingMeeting.notes };
    const updatedFormData = { ...formData, meetings: newMeetings };
    if (!await saveActivityToDb(updatedFormData)) return;
    setEditingMeeting(null);

  };

  // Comment handlers
  const handleAddComment = async () => {
    if (!newComment.trim()) return;
    const comment = { date: new Date().toISOString(), text: newComment };
    const updatedFormData = { ...formData, comments: [...formData.comments, comment] };
    if (!await saveActivityToDb(updatedFormData)) return;
    setNewComment("");

  };

  const handleEditComment = (index) => {
    const comment = formData.comments[index];
    setEditingComment({ index, text: comment.text });
  };

  const handleSaveCommentEdit = async () => {
    const newComments = [...formData.comments];
    newComments[editingComment.index] = { ...newComments[editingComment.index], text: editingComment.text };
    const updatedFormData = { ...formData, comments: newComments };
    if (!await saveActivityToDb(updatedFormData)) return;
    setEditingComment(null);

  };

  // Delete handlers
  const confirmDelete = (type, index) => {
    setDeleteConfirm({ open: true, type, index });
  };

  const handleDelete = async () => {
    const { type, index } = deleteConfirm;
    let updatedFormData;
    
    if (type === "call") {
      updatedFormData = { ...formData, call_logs: formData.call_logs.filter((_, i) => i !== index) };
    } else if (type === "text") {
      updatedFormData = { ...formData, text_logs: formData.text_logs.filter((_, i) => i !== index) };
    } else if (type === "meeting") {
      updatedFormData = { ...formData, meetings: formData.meetings.filter((_, i) => i !== index) };
    } else if (type === "scheduled_call") {
      updatedFormData = { ...formData, scheduled_calls: formData.scheduled_calls.filter((_, i) => i !== index) };
    } else if (type === "comment") {
      updatedFormData = { ...formData, comments: formData.comments.filter((_, i) => i !== index) };
    }
    
    if (!await saveActivityToDb(updatedFormData)) return;
    setDeleteConfirm({ open: false, type: null, index: null });

  };

  const handleSubmit = () => {
    if (!formData.company_name) {
      toast.error("Company Name is required");
      return;
    }
    onSave(leadDetails(formData, lead?.lead_id ? lead : null), false); // Save without closing
  };

  const handleSaveAndClose = () => {
    if (!formData.company_name) {
      toast.error("Company Name is required");
      return;
    }
    onSave(leadDetails(formData, lead?.lead_id ? lead : null), true); // Save and close
  };

  const formatDateTime = (dateStr) => {
    try {
      return format(new Date(dateStr), "MMM d, yyyy 'at' h:mm a");
    } catch {
      return dateStr;
    }
  };

  const getAllActivities = () => {
    const activities = [
      ...formData.call_logs.map((c, i) => ({ ...c, type: "call", originalIndex: i })),
      ...formData.text_logs.map((t, i) => ({ ...t, type: "text", originalIndex: i })),
      ...formData.meetings.map((m, i) => ({ ...m, type: "meeting", originalIndex: i })),
      ...formData.scheduled_calls.map((s, i) => ({ ...s, type: "scheduled_call", originalIndex: i })),
      ...formData.comments.map((c, i) => ({ ...c, type: "comment", originalIndex: i }))
    ];
    return activities.sort((a, b) => new Date(b.date) - new Date(a.date));
  };

  // Outcome selector component
  const OutcomeSelector = ({ value, onChange, disabled }) => (
    <div className="flex flex-wrap gap-2">
      {CALL_OUTCOMES.map(outcome => {
        const Icon = outcome.icon;
        const isSelected = value === outcome.value;
        return (
          <button
            key={outcome.value}
            type="button"
            disabled={disabled}
            onClick={() => onChange(isSelected ? "" : outcome.value)}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium border transition-all ${
              isSelected 
                ? outcome.color + " ring-2 ring-offset-1 ring-current" 
                : "bg-white border-slate-200 text-slate-600 hover:border-slate-300"
            }`}
          >
            <Icon className="w-4 h-4" />
            {outcome.label}
          </button>
        );
      })}
    </div>
  );

  // Outcome badge for display
  const OutcomeBadge = ({ outcome }) => {
    if (!outcome) return null;
    const display = getOutcomeDisplay(outcome);
    const Icon = display.icon;
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${display.color}`}>
        {Icon && <Icon className="w-3 h-3" />}
        {display.label}
      </span>
    );
  };

  return (
    <>
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className="sm:max-w-[95vw] max-h-[90vh] overflow-hidden p-0">
          <div className="flex h-[85vh]">
            {/* Left Sidebar - Lead Info (1/4) */}
            <div className="w-1/4 border-r bg-slate-50 p-6 overflow-y-auto">
              <DialogHeader className="mb-6">
                <div className="flex items-center justify-between gap-2">
                  <DialogTitle className="text-xl">{lead ? "Edit Lead" : "Add Lead"}</DialogTitle>
                  <div className="flex items-center gap-1">
                    <Button 
                      onClick={handleSubmit}
                      disabled={savingActivity}
                      size="sm"
                      className="bg-indigo-600 hover:bg-indigo-700 text-white"
                      data-testid="update-lead-btn"
                    >
                      {lead ? "Save" : "Create"}
                    </Button>
                    {lead && (
                      <Button 
                        onClick={handleSaveAndClose}
                        disabled={savingActivity}
                        size="sm"
                        variant="outline"
                        className="border-indigo-300 text-indigo-600 hover:bg-indigo-50"
                        data-testid="save-close-lead-btn"
                      >
                        Save & Close
                      </Button>
                    )}
                    {lead && leads.length > 1 && (
                      <>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={handlePreviousLead}
                          disabled={!hasPreviousLead || savingActivity}
                          className="text-slate-600 hover:text-indigo-600 disabled:opacity-30 px-2"
                          data-testid="prev-lead-btn"
                        >
                          <ChevronLeft className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={handleNextLead}
                          disabled={!hasNextLead || savingActivity}
                          className="text-slate-600 hover:text-indigo-600 disabled:opacity-30 px-2"
                          data-testid="next-lead-btn"
                        >
                          <ChevronRight className="w-4 h-4" />
                        </Button>
                      </>
                    )}
                  </div>
                </div>
              </DialogHeader>
              {saveError && <p role="alert" className="mb-4 text-sm text-red-700">{saveError}</p>}

              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Label className="text-xs text-slate-500">Status</Label>
                    <Select 
                      value={formData.status} 
                      onValueChange={v => setFormData({ ...formData, status: v })}
                      disabled={isStatusLocked}
                    >
                      <SelectTrigger className={`mt-1 ${isStatusLocked ? 'bg-slate-100' : ''}`}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {getAvailableStatuses().map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    {isStatusLocked && (
                      <p className="text-xs text-amber-600 mt-1">Managers only</p>
                    )}
                  </div>
                  <div>
                    <Label className="text-xs text-slate-500">Category</Label>
                    <MultiSelectDropdown
                      value={formData.category}
                      onChange={(categories) => setFormData({ ...formData, category: categories })}
                      options={CATEGORIES}
                      placeholder="Select categories..."
                      className="mt-1"
                    />
                  </div>
                </div>

                <div>
                  <Label className="text-xs text-slate-500">Company Name *</Label>
                  <Input value={formData.company_name} onChange={e => setFormData({ ...formData, company_name: e.target.value })} className="mt-1" />
                </div>

                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Label className="text-xs text-slate-500">First Name</Label>
                    <Input value={formData.first_name} onChange={e => setFormData({ ...formData, first_name: e.target.value })} className="mt-1" />
                  </div>
                  <div>
                    <Label className="text-xs text-slate-500">Last Name</Label>
                    <Input value={formData.last_name} onChange={e => setFormData({ ...formData, last_name: e.target.value })} className="mt-1" />
                  </div>
                </div>

                <div>
                  <Label className="text-xs text-slate-500">Title</Label>
                  <Input value={formData.title} onChange={e => setFormData({ ...formData, title: e.target.value })} className="mt-1" />
                </div>

                <div>
                  <Label className="text-xs text-slate-500">Mobile</Label>
                  <div className="flex items-center gap-2 mt-1">
                    <Input value={formData.mobile} onChange={e => setFormData({ ...formData, mobile: e.target.value })} className="flex-1" />
                    {formData.mobile && (
                      <a
                        href={`tel:${formData.mobile.replace(/\s/g, '')}`}
                        className="p-2 rounded-md bg-emerald-100 text-emerald-600 hover:bg-emerald-200 transition-colors"
                        title="Call mobile"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <Phone className="w-4 h-4" />
                      </a>
                    )}
                  </div>
                </div>

                <div>
                  <Label className="text-xs text-slate-500">Phone</Label>
                  <div className="flex items-center gap-2 mt-1">
                    <Input value={formData.phone_number} onChange={e => setFormData({ ...formData, phone_number: e.target.value })} className="flex-1" />
                    {formData.phone_number && (
                      <a
                        href={`tel:${formData.phone_number.replace(/\s/g, '')}`}
                        className="p-2 rounded-md bg-emerald-100 text-emerald-600 hover:bg-emerald-200 transition-colors"
                        title="Call phone"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <Phone className="w-4 h-4" />
                      </a>
                    )}
                  </div>
                </div>

                <div>
                  <Label className="text-xs text-slate-500">Email</Label>
                  <Input type="email" value={formData.email} onChange={e => setFormData({ ...formData, email: e.target.value })} className="mt-1" />
                </div>

                <div>
                  <Label className="text-xs text-slate-500">Website</Label>
                  <div className="flex items-center gap-2 mt-1">
                    <Input value={formData.website} onChange={e => setFormData({ ...formData, website: e.target.value })} className="flex-1" placeholder="https://" />
                    {formData.website && (
                      <a
                        href={formData.website.startsWith('http') ? formData.website : `https://${formData.website}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="p-2 rounded-md bg-blue-100 text-blue-600 hover:bg-blue-200 transition-colors"
                        title="Open website"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <Globe className="w-4 h-4" />
                      </a>
                    )}
                  </div>
                </div>

                <div>
                  <Label className="text-xs text-slate-500">Instagram</Label>
                  <div className="flex items-center gap-2 mt-1">
                    <Input value={formData.instagram} onChange={e => setFormData({ ...formData, instagram: e.target.value })} className="flex-1" placeholder="@username" />
                    {formData.instagram && (
                      <a
                        href={formData.instagram.startsWith('http') ? formData.instagram : `https://instagram.com/${formData.instagram.replace('@', '')}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="p-2 rounded-md bg-pink-100 text-pink-600 hover:bg-pink-200 transition-colors"
                        title="Open Instagram"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <Instagram className="w-4 h-4" />
                      </a>
                    )}
                  </div>
                </div>

                <div>
                  <Label className="text-xs text-slate-500">Facebook</Label>
                  <div className="flex items-center gap-2 mt-1">
                    <Input value={formData.facebook} onChange={e => setFormData({ ...formData, facebook: e.target.value })} className="flex-1" />
                    {formData.facebook && (
                      <a
                        href={formData.facebook.startsWith('http') ? formData.facebook : `https://facebook.com/${formData.facebook}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="p-2 rounded-md bg-blue-100 text-blue-700 hover:bg-blue-200 transition-colors"
                        title="Open Facebook"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <ExternalLink className="w-4 h-4" />
                      </a>
                    )}
                  </div>
                </div>

                {userRole === "manager" && teamMembers.length > 0 && (
                  <div>
                    <Label className="text-xs text-slate-500">Assigned To</Label>
                    {/* assigned_to is an array on most leads (a string on older ones);
                        picking a member here replaces the whole assignment. */}
                    <Select value={Array.isArray(formData.assigned_to) ? (formData.assigned_to[0] || "") : formData.assigned_to} onValueChange={v => {
                      const member = teamMembers.find(m => m.user_id === v);
                      setFormData({ ...formData, assigned_to: v, assigned_to_name: member?.name || "" });
                    }}>
                      <SelectTrigger className="mt-1"><SelectValue placeholder="Select team member" /></SelectTrigger>
                      <SelectContent>
                        {teamMembers.map(m => <SelectItem key={m.user_id} value={m.user_id}>{m.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    {Array.isArray(formData.assigned_to) && formData.assigned_to.length > 1 && (
                      <p className="text-xs text-slate-500 mt-1" data-testid="lead-multi-assignee-note">
                        Assigned to {formData.assigned_to.length} people. Picking one here replaces them all.
                      </p>
                    )}
                  </div>
                )}

                <div>
                  <Label className="text-xs text-slate-500">Company Notes</Label>
                  <Textarea 
                    value={formData.notes} 
                    onChange={e => setFormData({ ...formData, notes: e.target.value })} 
                    className="mt-1" 
                    placeholder="Add notes about this company..."
                    rows={4}
                    data-testid="lead-notes"
                  />
                </div>
              </div>
            </div>

            {/* Right Panel - Activity Logs (3/4) */}
            <div className="w-3/4 flex flex-col">
              {/* Tab Buttons */}
              <div className="flex gap-2 p-4 border-b bg-white items-center">
                <Button variant={activeTab === "calls" ? "default" : "outline"} onClick={() => setActiveTab("calls")} className={activeTab === "calls" ? "bg-indigo-600" : ""}>
                  <Phone className="w-4 h-4 mr-2" />Call Log ({formData.call_logs.length})
                </Button>
                <Button variant={activeTab === "schedule_call" ? "default" : "outline"} onClick={() => setActiveTab("schedule_call")} className={activeTab === "schedule_call" ? "bg-blue-600" : "border-blue-300 text-blue-700 hover:bg-blue-50"}>
                  <PhoneCall className="w-4 h-4 mr-2" />Schedule Call {formData.scheduled_calls.length > 0 && `(${formData.scheduled_calls.length})`}
                </Button>
                <Button variant={activeTab === "texts" ? "default" : "outline"} onClick={() => setActiveTab("texts")} className={activeTab === "texts" ? "bg-indigo-600" : ""}>
                  <MessageSquare className="w-4 h-4 mr-2" />Text Messages ({formData.text_logs.length})
                </Button>
                <Button variant={activeTab === "meetings" ? "default" : "outline"} onClick={() => setActiveTab("meetings")} className={activeTab === "meetings" ? "bg-indigo-600" : ""}>
                  <Calendar className="w-4 h-4 mr-2" />Meetings ({formData.meetings.length})
                </Button>
                <Button variant={activeTab === "comments" ? "default" : "outline"} onClick={() => setActiveTab("comments")} className={activeTab === "comments" ? "bg-indigo-600" : ""}>
                  <MessageCircle className="w-4 h-4 mr-2" />Comments ({formData.comments.length})
                </Button>
                <Button variant={activeTab === "all" ? "default" : "outline"} onClick={() => setActiveTab("all")} className={activeTab === "all" ? "bg-indigo-600" : ""}>
                  All Activity
                </Button>
                <Button variant={activeTab === "status_feed" ? "default" : "outline"} onClick={() => setActiveTab("status_feed")} className={activeTab === "status_feed" ? "bg-slate-700" : "border-slate-300 text-slate-700 hover:bg-slate-50"}>
                  <History className="w-4 h-4 mr-2" />Status Feed {formData.status_history.length > 0 && `(${formData.status_history.length})`}
                </Button>
                {activityError && (
                  <p role="alert" className="text-sm text-red-700 p-3 bg-red-50 rounded">
                    {activityError} Copy any draft text you want to keep before reopening.
                  </p>
                )}
                {savingActivity && (
                  <div className="ml-auto flex items-center gap-2 text-sm text-slate-500">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Saving...
                  </div>
                )}
              </div>

              {/* Activity Content */}
              <div className="flex-1 overflow-y-auto p-6 bg-slate-50">
                {/* Call Log Tab */}
                {activeTab === "calls" && (
                  <div className="space-y-4">
                    {/* Log a Call */}
                    <div className="bg-white rounded-lg p-4 border shadow-sm">
                      <h3 className="font-semibold mb-3 flex items-center gap-2"><Plus className="w-4 h-4" />Log a Call</h3>
                      <div className="mb-3">
                        <Label className="text-xs text-slate-500 mb-2 block">Direction</Label>
                        <div className="flex gap-2">
                          <button
                            type="button"
                            onClick={() => setNewCallDirection("outbound")}
                            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium border transition-all ${
                              newCallDirection === "outbound" 
                                ? "bg-indigo-100 text-indigo-700 border-indigo-300 ring-2 ring-offset-1 ring-indigo-400" 
                                : "bg-white border-slate-200 text-slate-600 hover:border-slate-300"
                            }`}
                          >
                            <ArrowUpRight className="w-4 h-4" />Outbound
                          </button>
                          <button
                            type="button"
                            onClick={() => setNewCallDirection("inbound")}
                            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium border transition-all ${
                              newCallDirection === "inbound" 
                                ? "bg-emerald-100 text-emerald-700 border-emerald-300 ring-2 ring-offset-1 ring-emerald-400" 
                                : "bg-white border-slate-200 text-slate-600 hover:border-slate-300"
                            }`}
                          >
                            <ArrowDownLeft className="w-4 h-4" />Inbound
                          </button>
                        </div>
                      </div>
                      <div className="mb-3">
                        <Label className="text-xs text-slate-500 mb-2 block">Call Outcome *</Label>
                        <OutcomeSelector value={newCallOutcome} onChange={setNewCallOutcome} disabled={savingActivity} />
                      </div>
                      <MentionSelector value={newCallNote} onChange={setNewCallNote} placeholder="Additional notes (optional, type @ to mention)..." rows={2} className="mb-3" />
                      <Button onClick={handleAddCall} disabled={!newCallOutcome || savingActivity} className="bg-indigo-600 hover:bg-indigo-700">
                        {savingActivity ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />}
                        Add Call Log
                      </Button>
                      {!lead && <p className="text-xs text-amber-600 mt-2">Note: Create the lead first to auto-save activities</p>}
                    </div>

                    {/* Call Logs List */}
                    {formData.call_logs.length > 0 && formData.call_logs.slice().reverse().map((call, reversedIndex) => {
                      const actualIndex = formData.call_logs.length - 1 - reversedIndex;
                      return editingCall?.index === actualIndex ? (
                        <div key={actualIndex} className="bg-white rounded-lg p-4 border border-indigo-300 shadow-sm">
                          <h4 className="font-semibold mb-3">Edit Call Log</h4>
                          <div className="mb-3">
                            <Label className="text-xs text-slate-500 mb-2 block">Direction</Label>
                            <div className="flex gap-2">
                              <button
                                type="button"
                                onClick={() => setEditingCall({ ...editingCall, direction: "outbound" })}
                                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium border transition-all ${
                                  editingCall.direction === "outbound" 
                                    ? "bg-indigo-100 text-indigo-700 border-indigo-300 ring-2 ring-offset-1 ring-indigo-400" 
                                    : "bg-white border-slate-200 text-slate-600 hover:border-slate-300"
                                }`}
                              >
                                <ArrowUpRight className="w-4 h-4" />Outbound
                              </button>
                              <button
                                type="button"
                                onClick={() => setEditingCall({ ...editingCall, direction: "inbound" })}
                                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium border transition-all ${
                                  editingCall.direction === "inbound" 
                                    ? "bg-emerald-100 text-emerald-700 border-emerald-300 ring-2 ring-offset-1 ring-emerald-400" 
                                    : "bg-white border-slate-200 text-slate-600 hover:border-slate-300"
                                }`}
                              >
                                <ArrowDownLeft className="w-4 h-4" />Inbound
                              </button>
                            </div>
                          </div>
                          <div className="mb-3">
                            <Label className="text-xs text-slate-500 mb-2 block">Call Outcome</Label>
                            <OutcomeSelector value={editingCall.outcome} onChange={v => setEditingCall({ ...editingCall, outcome: v })} disabled={savingActivity} />
                          </div>
                          <Textarea value={editingCall.notes} onChange={e => setEditingCall({ ...editingCall, notes: e.target.value })} rows={2} className="mb-3" placeholder="Additional notes..." />
                          <div className="flex gap-2">
                            <Button onClick={handleSaveCallEdit} disabled={savingActivity} className="bg-indigo-600 hover:bg-indigo-700">
                              {savingActivity ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}Save
                            </Button>
                            <Button variant="outline" onClick={() => setEditingCall(null)}>Cancel</Button>
                          </div>
                        </div>
                      ) : (
                        <div key={actualIndex} className="bg-white rounded-lg p-4 border group">
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center gap-2 text-sm text-slate-500">
                              {call.direction === "inbound" ? <ArrowDownLeft className="w-4 h-4 text-emerald-600" /> : <ArrowUpRight className="w-4 h-4 text-indigo-600" />}
                              <Phone className="w-4 h-4" />
                              <Clock className="w-3 h-3" />
                              {formatDateTime(call.date)}
                              <span className={`px-2 py-0.5 rounded text-xs ${call.direction === "inbound" ? "bg-emerald-100 text-emerald-700" : "bg-indigo-100 text-indigo-700"}`}>
                                {call.direction === "inbound" ? "Inbound" : "Outbound"}
                              </span>
                              <OutcomeBadge outcome={call.outcome} />
                              {/* Legacy support for old answered/interested fields */}
                              {!call.outcome && call.answered && <span className="bg-green-100 text-green-700 px-2 py-0.5 rounded text-xs">Answered</span>}
                              {!call.outcome && call.interested && <span className="bg-blue-100 text-blue-700 px-2 py-0.5 rounded text-xs">Interested</span>}
                              {/* Show logged by name - managers only */}
                              {call.logged_by_name && (
                                <span className="text-xs text-slate-400 italic">by {call.logged_by_name}</span>
                              )}
                            </div>
                            <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                              <Button variant="ghost" size="sm" onClick={() => handleEditCall(actualIndex)} className="h-8 w-8 p-0"><Pencil className="w-4 h-4" /></Button>
                              <Button variant="ghost" size="sm" onClick={() => confirmDelete("call", actualIndex)} className="h-8 w-8 p-0 text-red-500 hover:text-red-700"><Trash2 className="w-4 h-4" /></Button>
                            </div>
                          </div>
                          {call.notes && <p className="text-slate-700">{call.notes}</p>}
                        </div>
                      );
                    })}
                    
                    {formData.call_logs.length === 0 && (
                      <p className="text-slate-400 text-center py-8">No calls logged yet</p>
                    )}
                  </div>
                )}

                {/* Schedule Call Tab */}
                {activeTab === "schedule_call" && (
                  <div className="space-y-4">
                    {/* Schedule a Call Form */}
                    <div className="bg-white rounded-lg p-4 border shadow-sm border-blue-200">
                      <h3 className="font-semibold mb-3 flex items-center gap-2 text-blue-700">
                        <PhoneCall className="w-4 h-4" />Schedule a Call
                      </h3>
                      <div className="grid grid-cols-2 gap-3 mb-3">
                        <div>
                          <Label className="text-xs">Date *</Label>
                          <Input type="date" value={newScheduledCallDate} onChange={e => setNewScheduledCallDate(e.target.value)} className="mt-1" />
                        </div>
                        <div>
                          <Label className="text-xs">Time</Label>
                          <Input type="time" value={newScheduledCallTime} onChange={e => setNewScheduledCallTime(e.target.value)} className="mt-1" />
                        </div>
                      </div>
                      <Textarea value={newScheduledCallNote} onChange={e => setNewScheduledCallNote(e.target.value)} placeholder="Call notes or agenda..." rows={2} className="mb-3" />
                      <Button onClick={handleAddScheduledCall} disabled={!newScheduledCallDate || savingActivity} className="bg-blue-600 hover:bg-blue-700">
                        {savingActivity ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <PhoneCall className="w-4 h-4 mr-2" />}
                        Schedule Call
                      </Button>
                      {!lead && <p className="text-xs text-amber-600 mt-2">Note: Create the lead first to auto-save activities</p>}
                    </div>

                    {/* Scheduled Calls List */}
                    {formData.scheduled_calls.length > 0 && formData.scheduled_calls.slice().reverse().map((call, reversedIndex) => {
                      const actualIndex = formData.scheduled_calls.length - 1 - reversedIndex;
                      return editingScheduledCall?.index === actualIndex ? (
                        <div key={`scheduled-${actualIndex}`} className="bg-white rounded-lg p-4 border border-blue-300 shadow-sm">
                          <h4 className="font-semibold mb-3">Edit Scheduled Call</h4>
                          <div className="grid grid-cols-2 gap-3 mb-3">
                            <div>
                              <Label className="text-xs">Date</Label>
                              <Input type="date" value={editingScheduledCall.date} onChange={e => setEditingScheduledCall({ ...editingScheduledCall, date: e.target.value })} className="mt-1" />
                            </div>
                            <div>
                              <Label className="text-xs">Time</Label>
                              <Input type="time" value={editingScheduledCall.time} onChange={e => setEditingScheduledCall({ ...editingScheduledCall, time: e.target.value })} className="mt-1" />
                            </div>
                          </div>
                          <Textarea value={editingScheduledCall.notes} onChange={e => setEditingScheduledCall({ ...editingScheduledCall, notes: e.target.value })} rows={2} className="mb-3" />
                          <div className="flex gap-2">
                            <Button onClick={handleSaveScheduledCallEdit} disabled={savingActivity} className="bg-blue-600 hover:bg-blue-700">
                              {savingActivity ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}Save
                            </Button>
                            <Button variant="outline" onClick={() => setEditingScheduledCall(null)}>Cancel</Button>
                          </div>
                        </div>
                      ) : (
                        <div key={`scheduled-${actualIndex}`} className="bg-blue-50 rounded-lg p-4 border border-blue-200 group">
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center gap-2 text-sm text-blue-700">
                              <PhoneCall className="w-4 h-4" />
                              <Clock className="w-3 h-3" />
                              {formatDateTime(call.date)}
                              {new Date(call.date) > new Date() && <span className="bg-blue-100 text-blue-700 px-2 py-0.5 rounded text-xs">Upcoming</span>}
                            </div>
                            <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                              <Button variant="ghost" size="sm" onClick={() => handleEditScheduledCall(actualIndex)} className="h-8 w-8 p-0"><Pencil className="w-4 h-4" /></Button>
                              <Button variant="ghost" size="sm" onClick={() => confirmDelete("scheduled_call", actualIndex)} className="h-8 w-8 p-0 text-red-500 hover:text-red-700"><Trash2 className="w-4 h-4" /></Button>
                            </div>
                          </div>
                          {call.notes && <p className="text-slate-700">{call.notes}</p>}
                        </div>
                      );
                    })}

                    {formData.scheduled_calls.length === 0 && (
                      <p className="text-slate-400 text-center py-8">No scheduled calls yet</p>
                    )}
                  </div>
                )}

                {/* Text Messages Tab */}
                {activeTab === "texts" && (
                  <div className="space-y-4">
                    <div className="bg-white rounded-lg p-4 border shadow-sm">
                      <h3 className="font-semibold mb-3 flex items-center gap-2"><Plus className="w-4 h-4" />Log a Text Message</h3>
                      <MentionSelector value={newTextNote} onChange={setNewTextNote} placeholder="What was the message about? (type @ to mention)" rows={3} className="mb-3" />
                      <Button onClick={handleAddText} disabled={!newTextNote.trim() || savingActivity} className="bg-indigo-600 hover:bg-indigo-700">
                        {savingActivity ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />}
                        Add Text Log
                      </Button>
                      {!lead && <p className="text-xs text-amber-600 mt-2">Note: Create the lead first to auto-save activities</p>}
                    </div>

                    {formData.text_logs.slice().reverse().map((text, reversedIndex) => {
                      const actualIndex = formData.text_logs.length - 1 - reversedIndex;
                      return editingText?.index === actualIndex ? (
                        <div key={actualIndex} className="bg-white rounded-lg p-4 border border-indigo-300 shadow-sm">
                          <h4 className="font-semibold mb-3">Edit Text Message</h4>
                          <Textarea value={editingText.notes} onChange={e => setEditingText({ ...editingText, notes: e.target.value })} rows={3} className="mb-3" />
                          <div className="flex gap-2">
                            <Button onClick={handleSaveTextEdit} disabled={savingActivity} className="bg-indigo-600 hover:bg-indigo-700">
                              {savingActivity ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}Save
                            </Button>
                            <Button variant="outline" onClick={() => setEditingText(null)}>Cancel</Button>
                          </div>
                        </div>
                      ) : (
                        <div key={actualIndex} className="bg-white rounded-lg p-4 border group">
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center gap-2 text-sm text-slate-500">
                              <MessageSquare className="w-4 h-4" />
                              <Clock className="w-3 h-3" />
                              {formatDateTime(text.date)}
                              {/* Show logged by name - managers only */}
                              {text.logged_by_name && (
                                <span className="text-xs text-slate-400 italic">by {text.logged_by_name}</span>
                              )}
                            </div>
                            <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                              <Button variant="ghost" size="sm" onClick={() => handleEditText(actualIndex)} className="h-8 w-8 p-0"><Pencil className="w-4 h-4" /></Button>
                              <Button variant="ghost" size="sm" onClick={() => confirmDelete("text", actualIndex)} className="h-8 w-8 p-0 text-red-500 hover:text-red-700"><Trash2 className="w-4 h-4" /></Button>
                            </div>
                          </div>
                          <p className="text-slate-700">{text.notes}</p>
                        </div>
                      );
                    })}
                    {formData.text_logs.length === 0 && <p className="text-slate-400 text-center py-8">No text messages logged yet</p>}
                  </div>
                )}

                {/* Meetings Tab */}
                {activeTab === "meetings" && (
                  <div className="space-y-4">
                    <div className="bg-white rounded-lg p-4 border shadow-sm">
                      <h3 className="font-semibold mb-3 flex items-center gap-2"><Plus className="w-4 h-4" />Schedule a Meeting</h3>
                      <div className="grid grid-cols-2 gap-3 mb-3">
                        <div>
                          <Label className="text-xs">Date *</Label>
                          <Input type="date" value={newMeetingDate} onChange={e => setNewMeetingDate(e.target.value)} className="mt-1" />
                        </div>
                        <div>
                          <Label className="text-xs">Time</Label>
                          <Input type="time" value={newMeetingTime} onChange={e => setNewMeetingTime(e.target.value)} className="mt-1" />
                        </div>
                      </div>
                      <Textarea value={newMeetingNote} onChange={e => setNewMeetingNote(e.target.value)} placeholder="Meeting notes or agenda..." rows={2} className="mb-3" />
                      <Button onClick={handleAddMeeting} disabled={!newMeetingDate || savingActivity} className="bg-indigo-600 hover:bg-indigo-700">
                        {savingActivity ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />}
                        Add Meeting
                      </Button>
                      {!lead && <p className="text-xs text-amber-600 mt-2">Note: Create the lead first to auto-save activities</p>}
                    </div>

                    {formData.meetings.slice().reverse().map((meeting, reversedIndex) => {
                      const actualIndex = formData.meetings.length - 1 - reversedIndex;
                      return editingMeeting?.index === actualIndex ? (
                        <div key={actualIndex} className="bg-white rounded-lg p-4 border border-indigo-300 shadow-sm">
                          <h4 className="font-semibold mb-3">Edit Meeting</h4>
                          <div className="grid grid-cols-2 gap-3 mb-3">
                            <div>
                              <Label className="text-xs">Date</Label>
                              <Input type="date" value={editingMeeting.date} onChange={e => setEditingMeeting({ ...editingMeeting, date: e.target.value })} className="mt-1" />
                            </div>
                            <div>
                              <Label className="text-xs">Time</Label>
                              <Input type="time" value={editingMeeting.time} onChange={e => setEditingMeeting({ ...editingMeeting, time: e.target.value })} className="mt-1" />
                            </div>
                          </div>
                          <Textarea value={editingMeeting.notes} onChange={e => setEditingMeeting({ ...editingMeeting, notes: e.target.value })} rows={2} className="mb-3" />
                          <div className="flex gap-2">
                            <Button onClick={handleSaveMeetingEdit} disabled={savingActivity} className="bg-indigo-600 hover:bg-indigo-700">
                              {savingActivity ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}Save
                            </Button>
                            <Button variant="outline" onClick={() => setEditingMeeting(null)}>Cancel</Button>
                          </div>
                        </div>
                      ) : (
                        <div key={actualIndex} className="bg-white rounded-lg p-4 border group">
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center gap-2 text-sm text-slate-500">
                              <Calendar className="w-4 h-4" />
                              <Clock className="w-3 h-3" />
                              {formatDateTime(meeting.date)}
                              {new Date(meeting.date) > new Date() && <span className="bg-amber-100 text-amber-700 px-2 py-0.5 rounded text-xs">Upcoming</span>}
                              {/* Show logged by name - managers only */}
                              {meeting.logged_by_name && (
                                <span className="text-xs text-slate-400 italic">by {meeting.logged_by_name}</span>
                              )}
                            </div>
                            <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                              <Button variant="ghost" size="sm" onClick={() => handleEditMeeting(actualIndex)} className="h-8 w-8 p-0"><Pencil className="w-4 h-4" /></Button>
                              <Button variant="ghost" size="sm" onClick={() => confirmDelete("meeting", actualIndex)} className="h-8 w-8 p-0 text-red-500 hover:text-red-700"><Trash2 className="w-4 h-4" /></Button>
                            </div>
                          </div>
                          {meeting.notes && <p className="text-slate-700">{meeting.notes}</p>}
                        </div>
                      );
                    })}
                    {formData.meetings.length === 0 && <p className="text-slate-400 text-center py-8">No meetings scheduled yet</p>}
                  </div>
                )}

                {/* Comments Tab */}
                {activeTab === "comments" && (
                  <div className="space-y-4">
                    {/* Add Comment */}
                    <div className="bg-white rounded-lg p-4 border">
                      <h4 className="font-medium text-slate-700 flex items-center gap-2 mb-3">
                        <Plus className="w-4 h-4" />
                        Add Comment
                      </h4>
                      <MentionSelector 
                        value={newComment} 
                        onChange={setNewComment} 
                        placeholder="Add a general comment about this lead... (type @ to mention someone)"
                        rows={3}
                        className="mb-3"
                      />
                      <Button 
                        onClick={handleAddComment} 
                        disabled={!newComment.trim() || savingActivity}
                        className="bg-indigo-600 hover:bg-indigo-700"
                      >
                        {savingActivity ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />}
                        Add Comment
                      </Button>
                    </div>

                    {/* Comments List */}
                    {[...formData.comments].reverse().map((comment, index) => {
                      const actualIndex = formData.comments.length - 1 - index;
                      return editingComment?.index === actualIndex ? (
                        <div key={actualIndex} className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                          <h4 className="font-medium text-amber-800 mb-3">Editing Comment</h4>
                          <Textarea 
                            value={editingComment.text} 
                            onChange={e => setEditingComment({ ...editingComment, text: e.target.value })} 
                            rows={3} 
                            className="mb-3" 
                          />
                          <div className="flex gap-2">
                            <Button onClick={handleSaveCommentEdit} disabled={savingActivity} className="bg-indigo-600 hover:bg-indigo-700">
                              {savingActivity ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}Save
                            </Button>
                            <Button variant="outline" onClick={() => setEditingComment(null)}>Cancel</Button>
                          </div>
                        </div>
                      ) : (
                        <div key={actualIndex} className="bg-white rounded-lg p-4 border group">
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center gap-2 text-sm text-slate-500">
                              <MessageCircle className="w-4 h-4" />
                              <Clock className="w-3 h-3" />
                              {formatDateTime(comment.date)}
                              {/* Show logged by name - managers only */}
                              {comment.logged_by_name && (
                                <span className="text-xs text-slate-400 italic">by {comment.logged_by_name}</span>
                              )}
                            </div>
                            <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                              <Button variant="ghost" size="sm" onClick={() => handleEditComment(actualIndex)} className="h-8 w-8 p-0"><Pencil className="w-4 h-4" /></Button>
                              <Button variant="ghost" size="sm" onClick={() => confirmDelete("comment", actualIndex)} className="h-8 w-8 p-0 text-red-500 hover:text-red-700"><Trash2 className="w-4 h-4" /></Button>
                            </div>
                          </div>
                          <p className="text-slate-700">{comment.text}</p>
                        </div>
                      );
                    })}
                    {formData.comments.length === 0 && <p className="text-slate-400 text-center py-8">No comments yet</p>}
                  </div>
                )}

                {/* All Activity Tab */}
                {activeTab === "all" && (
                  <div className="space-y-4">
                    {getAllActivities().map((activity, i) => (
                      <div key={i} className={`rounded-lg p-4 border group ${activity.type === 'scheduled_call' ? 'bg-blue-50 border-blue-200' : 'bg-white'}`}>
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-2 text-sm text-slate-500 flex-wrap">
                            {activity.type === "call" && <Phone className="w-4 h-4 text-green-600" />}
                            {activity.type === "scheduled_call" && <PhoneCall className="w-4 h-4 text-blue-600" />}
                            {activity.type === "text" && <MessageSquare className="w-4 h-4 text-blue-600" />}
                            {activity.type === "meeting" && <Calendar className="w-4 h-4 text-amber-600" />}
                            {activity.type === "comment" && <MessageCircle className="w-4 h-4 text-purple-600" />}
                            <span className="font-medium capitalize">{activity.type === 'scheduled_call' ? 'Scheduled Call' : activity.type}</span>
                            <Clock className="w-3 h-3" />
                            {formatDateTime(activity.date)}
                            {activity.type === "call" && <OutcomeBadge outcome={activity.outcome} />}
                            {(activity.type === "meeting" || activity.type === "scheduled_call") && new Date(activity.date) > new Date() && <span className="bg-amber-100 text-amber-700 px-2 py-0.5 rounded text-xs">Upcoming</span>}
                            {/* Show logged by name - managers only */}
                            {activity.logged_by_name && (
                              <span className="text-xs text-slate-400 italic">by {activity.logged_by_name}</span>
                            )}
                          </div>
                          <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                            <Button variant="ghost" size="sm" onClick={() => {
                              if (activity.type === "call") { handleEditCall(activity.originalIndex); setActiveTab("calls"); }
                              else if (activity.type === "text") { handleEditText(activity.originalIndex); setActiveTab("texts"); }
                              else if (activity.type === "scheduled_call") { handleEditScheduledCall(activity.originalIndex); setActiveTab("schedule_call"); }
                              else if (activity.type === "comment") { handleEditComment(activity.originalIndex); setActiveTab("comments"); }
                              else { handleEditMeeting(activity.originalIndex); setActiveTab("meetings"); }
                            }} className="h-8 w-8 p-0"><Pencil className="w-4 h-4" /></Button>
                            <Button variant="ghost" size="sm" onClick={() => confirmDelete(activity.type, activity.originalIndex)} className="h-8 w-8 p-0 text-red-500 hover:text-red-700"><Trash2 className="w-4 h-4" /></Button>
                          </div>
                        </div>
                        {activity.notes && <p className="text-slate-700">{activity.notes}</p>}
                      </div>
                    ))}
                    {getAllActivities().length === 0 && <p className="text-slate-400 text-center py-8">No activity logged yet</p>}
                  </div>
                )}

                {/* Status Feed Tab */}
                {activeTab === "status_feed" && (
                  <div className="space-y-4">
                    <div className="bg-white rounded-lg p-4 border shadow-sm">
                      <h3 className="font-semibold text-slate-700 flex items-center gap-2 mb-2">
                        <History className="w-4 h-4" />
                        Status Change History
                      </h3>
                      <p className="text-sm text-slate-500">
                        Track the lifecycle of this lead's status changes. This feed is automatically updated when the status is changed and saved.
                      </p>
                    </div>

                    {[...formData.status_history].reverse().map((change, index) => (
                      <div key={index} className="bg-white rounded-lg p-4 border">
                        <div className="flex items-center gap-3 mb-2">
                          <div className="flex items-center gap-2">
                            <span className="px-2 py-1 rounded text-xs font-medium bg-slate-100 text-slate-600">
                              {change.from_status}
                            </span>
                            <span className="text-slate-400">→</span>
                            <span className="px-2 py-1 rounded text-xs font-medium bg-indigo-100 text-indigo-700">
                              {change.to_status}
                            </span>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 text-sm text-slate-500">
                          <Clock className="w-3 h-3" />
                          {formatDateTime(change.changed_at)}
                          {change.changed_by_name && (
                            <span className="text-xs text-slate-400 italic">by {change.changed_by_name}</span>
                          )}
                        </div>
                      </div>
                    ))}

                    {formData.status_history.length === 0 && (
                      <p className="text-slate-400 text-center py-8">No status changes recorded yet. Changes will appear here when the status is updated.</p>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Modal */}
      <Dialog open={deleteConfirm.open} onOpenChange={() => setDeleteConfirm({ open: false, type: null, index: null })}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="w-5 h-5" />
              Confirm Deletion
            </DialogTitle>
          </DialogHeader>
          <p className="text-slate-600 py-4">
            Are you sure you want to delete this {deleteConfirm.type === 'scheduled_call' ? 'scheduled call' : deleteConfirm.type}? This action cannot be undone.
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteConfirm({ open: false, type: null, index: null })}>
              Cancel
            </Button>
            <Button onClick={handleDelete} disabled={savingActivity} className="bg-red-600 hover:bg-red-700 text-white">
              {savingActivity ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default LeadModal;
